"use client";

import { Minus, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ImagePlaceholder } from "@/components/ui/image-placeholder";
import { Sheet } from "@/components/ui/sheet";
import { cn } from "@/lib/cn";
import type { TransactionProductDto } from "@/content/types";
import { formatMoney } from "@/lib/money";

export function SkuSheet({
  product,
  open,
  onOpenChange,
  initialInviteCode,
}: {
  product: TransactionProductDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialInviteCode: string;
}) {
  const [selectedSkuId, setSelectedSkuId] = useState(
    product.defaultSkuId ?? product.skus[0]?.id ?? "",
  );
  const [quantity, setQuantity] = useState(1);
  const [inviteCode, setInviteCode] = useState(product.slug === "jiuyuejie" ? "" : initialInviteCode);

  const selectedSku =
    product.skus.find((sku) => sku.id === selectedSkuId) ?? product.skus[0];
  const selectedMoney = selectedSku.prices[product.selectedCurrency] ?? null;
  const price = useMemo(
    () =>
      selectedMoney
        ? formatMoney(selectedMoney)
        : (product.availabilityCopy ?? "该规格暂未定价"),
    [selectedMoney, product.availabilityCopy],
  );

  const confirm = () => {
    const params = new URLSearchParams({
      slug: product.slug,
      sku: selectedSku.id,
      qty: String(quantity),
      ...(product.slug === "jiuyuejie" ? {} : { code: inviteCode }),
    });
    onOpenChange(false);
    window.location.assign(`/order/confirm?${params.toString()}`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="选择规格">
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <ImagePlaceholder
            label={`${product.name} 缩略图`}
            ratio="1:1"
            src={product.gallery[0]?.src}
            alt={product.gallery[0]?.alt}
            objectPosition={product.gallery[0]?.objectPosition}
            className="size-16 shrink-0 rounded-[10px]"
          />
          <div className="min-w-0">
            <p className="text-sm leading-6 font-semibold text-ink">{product.name}</p>
            <p className={selectedMoney ? "font-body text-2xl leading-7 font-bold text-cinnabar tabular-nums" : "font-body text-lg leading-7 font-semibold text-ink"}>
              {price}
            </p>
          </div>
        </div>

        <section>
          <h3 className="text-xs leading-4 font-semibold tracking-[0.08em] text-ink-3 uppercase">
            规格
          </h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {product.skus.map((sku) => {
              const active = sku.id === selectedSku.id;
              const singleSku = product.skus.length === 1;

              return (
                <button
                  key={sku.id}
                  type="button"
                  disabled={singleSku}
                  aria-pressed={active}
                  onClick={() => setSelectedSkuId(sku.id)}
                  className={cn(
                    "min-h-11 min-w-[120px] rounded-lg border px-3 py-2 text-left text-sm leading-5 transition-colors",
                    active
                      ? "border-forest-700 bg-forest-100 text-ink"
                      : "border-line bg-card text-ink",
                    singleSku && "cursor-default",
                  )}
                >
                  <span className="block">{sku.spec}</span>
                  <span className="mt-1 block font-mono text-[11px] leading-4 text-ink-3">
                    {sku.prices[product.selectedCurrency]
                      ? formatMoney(sku.prices[product.selectedCurrency]!)
                      : sku.active && sku.stockAvailable
                        ? "该规格暂未定价"
                        : !sku.active
                          ? "该规格未启用"
                          : "该规格缺货"}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section>
          <h3 className="text-xs leading-4 font-semibold tracking-[0.08em] text-ink-3 uppercase">
            数量
          </h3>
          <div className="mt-3 inline-flex items-center rounded-lg border border-line bg-card">
            <button
              type="button"
              aria-label="减少数量"
              disabled={quantity <= 1}
              onClick={() => setQuantity((value) => Math.max(1, value - 1))}
              className="flex size-11 items-center justify-center text-ink disabled:text-ink-3"
            >
              <Minus className="size-4 stroke-[1.5]" aria-hidden="true" />
            </button>
            <span className="flex min-w-12 items-center justify-center px-2 font-mono text-sm leading-5 text-ink">
              {quantity}
            </span>
            <button
              type="button"
              aria-label="增加数量"
              disabled={quantity >= 9}
              onClick={() => setQuantity((value) => Math.min(9, value + 1))}
              className="flex size-11 items-center justify-center text-ink disabled:text-ink-3"
            >
              <Plus className="size-4 stroke-[1.5]" aria-hidden="true" />
            </button>
          </div>
        </section>

        {product.slug !== "jiuyuejie" && <section>
          <label
            htmlFor="invite-code"
            className="text-xs leading-4 font-semibold tracking-[0.08em] text-ink-3 uppercase"
          >
            邀请码
          </label>
          <input
            id="invite-code"
            type="text"
            value={inviteCode}
            onChange={(event) => setInviteCode(event.target.value)}
            className="mt-3 h-11 w-full rounded-lg border border-line bg-card px-3 font-mono text-sm leading-5 text-ink outline-none placeholder:text-ink-3 focus:border-forest-500"
            placeholder="输入邀请码"
          />
          <p className="mt-2 text-xs leading-[18px] text-ink-3">
            邀请码用于建立邀请关系，不改变本单金额。
          </p>
        </section>}

        <Button
          variant="buy"
          className="w-full"
          onClick={confirm}
          disabled={!product.purchasable || !selectedMoney || !selectedSku.active || !selectedSku.stockAvailable}
        >
          {product.purchasable && selectedMoney ? "确认" : "该规格暂不可购买"}
        </Button>
      </div>
    </Sheet>
  );
}
